import { useCallback, useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { fetchUiSecrets, resetUiSecrets } from "utils/uiSecrets";
import { configureDataPondApiUrl, resetDataPondApiUrl } from "http/index";
import { applyUiRuntimeConfig, resetUiRuntimeConfig } from "utils/runtimeConfig";

export type UiSecretsStatus = "idle" | "loading" | "ready" | "error";

interface IUseUiSecretsResult {
  status: UiSecretsStatus;
  retry: () => void;
}

export const useUiSecrets = (): IUseUiSecretsResult => {
  const msalInstance = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const accountKey = msalInstance.accounts
    .map(account => account.homeAccountId)
    .join(",");
  const [status, setStatus] = useState<UiSecretsStatus>("idle");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || !accountKey) {
      setStatus("idle");
      resetUiSecrets();
      resetUiRuntimeConfig();
      resetDataPondApiUrl();
      return;
    }

    let isCurrent = true;
    setStatus("loading");

    fetchUiSecrets(msalInstance)
      .then(data => {
        if (!data?.secret_key_for_id) {
          throw new Error("UI secrets response is missing required values");
        }
        applyUiRuntimeConfig(data);
        configureDataPondApiUrl(data.api_url);
        if (isCurrent) setStatus("ready");
      })
      .catch(error => {
        if (isCurrent) {
          resetUiSecrets();
          resetUiRuntimeConfig();
          resetDataPondApiUrl();
          console.error("Unable to initialize UI settings:", error);
          setStatus("error");
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [accountKey, attempt, isAuthenticated, msalInstance.instance]);

  const retry = useCallback(() => setAttempt(current => current + 1), []);

  return { status, retry };
};